import { Button, Color, Flex, Modal, SectionMessage, Space, Text, useModal } from '@billy/design-system'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  useUpdateOrganizationSettings,
  useUserOrganization,
  useUserOrganizationSettings,
} from '@modules-deprecated/app/organization'

import { SpotClients } from '../../../../assets/images'
import { ModalId } from '../../../../enums/modalId'
import { formatDate } from '../../../../utils'
import { DmsUserRole } from '../../enums/dmsUserRole'

const SETTING = 'firstTimeInDocumentsModalViewedDate'

interface FirstTimeInDocumentsModalProps {
  role: DmsUserRole
}

export const FirstTimeInDocumentsModal = ({ role }: FirstTimeInDocumentsModalProps) => {
  const [displayModal, setDisplayModal] = useState(false)
  const { t } = useTranslation()
  const { organization } = useUserOrganization()
  const { organizationSettings } = useUserOrganizationSettings()
  const { update } = useUpdateOrganizationSettings()

  const isCpa = role === DmsUserRole.CpaClientMode || role === DmsUserRole.CpaExpertMode
  const titleForBO =
    role === DmsUserRole.BoWithCpa
      ? t('documents.modal.first_time_in_documents.title.bo_linked_to_cpa')
      : t('documents.modal.first_time_in_documents.title.bo_without_cpa')
  const descriptionForBO =
    role === DmsUserRole.BoWithCpa
      ? t('documents.modal.first_time_in_documents.description.bo_linked_to_cpa')
      : t('documents.modal.first_time_in_documents.description.bo_without_cpa')

  const updateSetting = useCallback(() => {
    if (organization?.id) {
      update({ key: SETTING, organizationId: organization.id, value: formatDate(new Date()) as string })
    }
  }, [organization?.id, update])

  const { open, close } = useModal(ModalId.DocumentsFirstTimeModal, {
    onClose: () => updateSetting(),
  })

  useEffect(() => {
    if (organizationSettings && !organizationSettings.firstTimeInDocumentsModalViewedDate) {
      setDisplayModal(true)
      open()
    }
  }, [organizationSettings, open])

  const handleClose = useCallback(() => {
    setDisplayModal(false)
    close()
  }, [close])

  if (!displayModal) {
    return null
  }

  return (
    <Modal id={ModalId.DocumentsFirstTimeModal} size="s" closable={false}>
      <Modal.Body>
        <Flex flexDirection="column" alignItems="center">
          <SpotClients width={200} height={200} />
        </Flex>
        <Space size="s32" />
        <Text variant="h3" color={Color.Grey950}>
          {isCpa ? t('documents.modal.first_time_in_documents.title.cpa') : titleForBO}
        </Text>
        <Space size="s16" />
        <Text variant="bodyMD" color={Color.Grey800}>
          {isCpa ? t('documents.modal.first_time_in_documents.description.cpa') : descriptionForBO}
        </Text>
        {!isCpa && (
          <>
            <Space size="s16" />
            <SectionMessage showIcon variant="warning">
              <Text variant="bodyMD">{t('documents.modal.first_time_in_documents.alert.bo_linked_or_not_to_cpa')}</Text>
            </SectionMessage>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={handleClose}>{t('documents.modal.first_time_in_documents.button')}</Button>
      </Modal.Footer>
    </Modal>
  )
}
